define([
    'backbone',
    'bignumber.js',
    'moment', // Using pure moment.
    'modules/common/models/localstorage',
    '@old/enums',
    '@old/utils'
], (Backbone, BigNumber, moment, LocalStorage, enums, Utils) => {

    const locales = new Backbone.Collection([
        {
            id: 'ru',
            name: 'Russian (RU)'
        }, {
            id: 'en',
            name: 'English (EN)'
        }
    ]);
    const localizedRegionsKey = 'regions_channel_{0}_locale_{1}';

    const AppContext = Backbone.Model.extend({
        cookiesExpireTime: 0xBAD,
        localStorageRegistry: {},
        routerDeferred: $.Deferred(),
        store: null, // global store

        routerLoaded () {
            return this.routerDeferred.promise();
        },

        fetchInitial () {
            const user = this.user();
            const channels = this.channels();
            const currencies = this.currencies();
            const roles = this.roles();
            const timezones = this.timezones();
            const releaseNotes = this.releaseNotes();
            const userNotes = this.userNotes();
            const userPermissions = this.permissions();
            const fetchRootAccounts = this.store.dispatch('FETCH_ROOT_ACCOUNTS');

            return Promise.all([
                user.isNew() ? user.fetch() : {},
                channels.length === 0 ? this.fetchChannels() : {},
                currencies.length === 0 ? currencies.fetch() : {},
                roles.length === 0 ? roles.fetch() : {},
                timezones.length === 0 ? timezones.fetch() : {},
                releaseNotes.length === 0 ? releaseNotes.fetch().
                    catch(() => Promise.resolve()) : {},
                userPermissions.length === 0 ? this.fetchPermissions() : {},
                userNotes.isNew() ? userNotes.fetch() : {},
                enums.columnPresetScopes.fetch(),
                fetchRootAccounts
            ]).
                catch((e) => {
                    console.warn('Failed to load inital state');
                    throw e;
                });
        },

        async fetchChannels () {
            await this.channels().
                fetch();
            const channels = this.channels().
                toJSON();
            this.store.dispatch('channels/SET_CHANNELS', channels);
        },

        async fetchEntity (id, entity, options) {
            const force = Boolean(options && options.force);
            if (Number(id) !== entity.get('id') || force) {
                if (Number(id) !== entity.get('id')) {
                    entity.clear();
                }
                if (this.idExists(id)) {
                    entity.set({id}, {silent: true});
                    await entity.fetch();
                    return entity;
                }
            }
            return id ? entity : null;
        },

        async fetchProject (id, options) {
            const project = await this.fetchEntity(id, this.project(), options);
            return await this.store.dispatch('projects/SET_CURRENT_PROJECT', project && project.toJSON() || null);
        },

        async fetchPlacement (id) {
            return await this.fetchEntity(id, this.placement());
        },

        async fetchConnection (id) {
            return await this.fetchEntity(id, this.connection());
        },

        async fetchOrder (id) {
            return await this.fetchEntity(id, this.order());
        },

        async fetchContract (id) {
            return await this.fetchEntity(id, this.contract());
        },

        async fetchBidManagerGroup (project_id, id) {

            // TODO: refactor group url!
            const group = this.bidManagerGroup();
            if (Number(id) !== group.get('id')) {
                group.clear().
                    set({project_id});
                if (this.idExists(id)) {
                    group.set({id}, {silent: true});
                    return await group.fetch();
                }
            }
        },

        has_component (component) {
            const components = _.wrapArray(component);
            const accountComponents = this.account().
                get('components');
            return _.intersection(components, accountComponents).length === components.length;
        },

        channelsNames () {
            return this.channels().
                pluck('display_name');
        },
        channelsIds () {
            return this.channels().
                pluck('id');
        },
        channelDisplayNameByName (name) {
            const id = this.channelIdByName(name);
            return this.channels().
                get(id).
                get('display_name');
        },
        channelById (id) {
            return this.channels().
                get(id);
        },

        channelIdByName (name) {
            return this.channelByName(name).id;
        },

        channelIdsByNames (channelNames) {
            return _.map(channelNames, function (channelName) {
                const channel = this.channelByName(channelName);

                // do fail if channel is not added to backend side
                if (!channel) {
                    console.error(`Channel with name [${channelName}] not present`);
                    return null;
                }
                return channel.id;
            }, this).
                filter(c => Boolean(c));
        },

        channelsWithFact () {
            return new Backbone.Collection(
                this.channels().
                    filter((channel) => channel.get('has_fact') && channel.get('is_enabled'), this)
            );
        },

        isChannelWithFact (channel_id) {
            return _.contains(this.channelsWithFact().
                pluck('id'), channel_id);
        },

        channelsWithPlan () {
            return new Backbone.Collection(
                this.channels().
                    filter((channel) => channel.get('has_plan') && channel.get('is_enabled'), this)
            );
        },

        isChannelWithPlan (channel_id) {
            return _.contains(this.channelsWithPlan().
                pluck('id'), channel_id);
        },

        channelsAreEnabled () {
            return new Backbone.Collection(
                this.channels().
                    filter((channel) => channel.get('is_enabled'), this)
            );
        },

        isChannelEnabled (channel_id) {
            return _.contains(this.channelsAreEnabled().
                pluck('id'), channel_id);
        },

        channelIdsByNamesEnabled (channelNames) {
            const self = this;
            return self.channelIdsByNames(channelNames).
                filter((id) => self.isChannelEnabled(id));
        },

        nonDirectChannels () {
            return this.channelIdsByNames(['facebook', 'googledcm', 'tiktok']);
        },
        directOnlyChannels () {
            return this.channelIdsByNames([]);
        },
        connectableChannels () {

            // Channels which can be connected to system. We can add placements from them.
            return new Backbone.Collection(this.channels().
                filter(function (channel) {
                    return !_.contains(this.trackingOnlyChannels(), channel.id);
                }, this));
        },
        oauthBasedChannels () {
            return this.channelIdsByNames([
                'yandex', 'google', 'facebook', 'vkontakte', 'youtube', 'yandexmarket', 'yandexdisplay', 'targetmail',
                'googledbm', 'googledcm', 'yandexaudience', 'twitter', 'tiktok', 'vkads'
            ]);
        },

        // oauth based channels, but user can select external account when creating connection for these ones
        oauthSelectChannels () {
            return this.store.getters['channels/oauthSelectChannels'].map(channel => channel.id);
        },
        credentialsBasedChannels () {
            return this.channelIdsByNames(['begun', 'weborama', 'adriver']);
        },
        tokenBasedChannels () {
            return this.channelIdsByNames(['rbmail', 'ozon']);
        },
        onlyTokenBasedChannels () {
            return this.channelIdsByNames(['adfox', 'wildberries', 'ozonseller']);
        },
        passportConnectionChannels () {
            return this.channelIdsByNames([
                'yandex', 'facebook', 'targetmail', 'yandexaudience', 'yandexmarket', 'googledcm', 'googledbm',
                'vkontakte', 'google', 'youtube', 'twitter', 'yandexdisplay', 'ozon', 'ozonseller', 'tiktok', 'wildberries', 'vkads'
            ]);
        },
        customBasedChannels () {
            return this.channelIdsByNames(['custom']);
        },
        balancingEnabledChannels () {
            return this.channelIdsByNamesEnabled(
                ['ozon', 'yandex', 'google', 'facebook', 'vkontakte', 'targetmail', 'vkads']
            );
        },
        socialOptimizationEnabledChannels () {
            return this.channelIdsByNamesEnabled(['facebook']);
        },
        bidManagerEnabledChannels () {
            return this.channelIdsByNamesEnabled(
                ['yandex', 'google', 'facebook', 'vkontakte', 'targetmail', 'ozon', 'vkads']
            );
        },
        taggableChannels () {
            return this.channelIdsByNamesEnabled(
                ['yandex', 'google', 'facebook', 'vkontakte', 'targetmail', 'yandexmarket', 'vkads']
            );
        },
        urlcheckEnabledChannels () {
            return this.channelIdsByNamesEnabled(['yandex', 'google']);
        },
        crossminusEnabledChannels () {
            return this.channelIdsByNamesEnabled(['yandex']);
        },
        reportsEnabledChannels () {
            return this.channelIdsByNames(
                [
                    'yandex', 'begun', 'google', 'youtube', 'facebook', 'vkontakte', 'targetmail', 'yandexdisplay',
                    'googledbm', 'adriver', 'ozon', 'ozonseller', 'tiktok', 'wildberries', 'vkads'
                ]
            );
        },
        generationEnabledChannels () {
            return this.channelIdsByNamesEnabled(['yandex', 'google']);
        },
        autoBindingChannels () {

            // allow autobinding placements to orders
            return this.channelIdsByNames([
                'yandex', 'google', 'facebook', 'vkontakte', 'targetmail', 'youtube',
                'googledcm', 'weborama', 'googledbm', 'yandexdisplay', 'rbmail', 'vkads'
            ]);
        },
        trackingChannels () {
            return this.trackingWithStatsChannels().
                concat(this.trackingOnlyChannels());
        },
        trackingOnlyChannels () {
            return this.channelIdsByNames(['adform']);
        },
        trackingWithStatsChannels () {
            return this.channelIdsByNames(['googledcm', 'weborama']);
        },
        manageBudgetChannels () {
            return this.channelIdsByNames([
                'yandex', 'google', 'begun', 'facebook', 'googledbm', 'targetmail',
                'vkontakte', 'yandexdisplay', 'vkads'
            ]);
        },
        detailedStatisticsChannels () {
            return this.channelIdsByNames([
                'yandex', 'google', 'facebook', 'vkontakte', 'targetmail',
                'adfox', 'yandexmarket', 'youtube', 'googledbm', 'googledcm', 'yandexdisplay', 'twitter', 'adriver',
                'custom', 'ozon', 'ozonseller', 'tiktok', 'wildberries', 'vkads'
            ]);
        },

        segmentsChannels () {
            return this.channelIdsByNamesEnabled(
                ['yandex', 'google', 'facebook', 'targetmail', 'vkontakte', 'vkads']
            );
        },

        waProviders () {
            throw new Error('Should be overrided');
        },

        waProviderIdByName (name) {
            return this.waProviders().
                find((provider) => provider.get('name') === name).id;
        },
        waProviderIdsByNames (providerNames) {
            return _.map(providerNames, function (provider) {
                return this.waProviderIdByName(provider);
            }, this);
        },
        oauthBasedWAProviders () {
            return this.waProviderIdsByNames(['googleanalytics', 'yandexmetrika', 'appmetrica']);
        },
        credentialsBasedWAProviders () {
            return this.waProviderIdsByNames(['comagic']);
        },
        credentialsSecretKeyBasedWAProviders () {
            return this.waProviderIdsByNames(['amocrm']);
        },
        passportWAConnectionProviders () {
            return this.waProviderIdsByNames(['appmetrica', 'amocrm', 'googleanalytics', 'yandexmetrika']);
        },
        storeHouseWAConnectionProviders () {
            return this.waProviderIdsByNames(['appmetrica', 'amocrm', 'googleanalytics']);
        },
        ipWhitelistedProvidersIds () {
            return this.waProviderIdsByNames(['comagic']);
        },

        locales () {
            return locales;
        },
        projectId () {
            return this.project().
                get('id');
        },
        placementId () {
            return this.placement().
                get('id');
        },
        orderId () {
            return this.order().
                get('id');
        },
        contractId () {
            return this.contract().
                get('id');
        },
        connectionId () {
            return this.connection().
                get('id');
        },
        bidManagerGroupId () {
            return this.bidManagerGroup().
                get('id');
        },

        yandexMKBPlacementTypesList () {
            return ['YANDEX_MCBANNER_CAMPAIGN', 'YANDEX_SMART_BANNER_CAMPAIGN', 'YANDEX_UNDEFINED_CAMPAIGN'];
        },

        ozonUnupportedPlacementTypesList () {
            return [];
        },

        ozonBMSupportedPlacementTypesList () {
            return ['OZON_PAY_PER_CLICK', 'OZON_BRAND_SHELF'];
        },

        adRiverFetchedPlacementTypesList () {
            return ['ADRIVER_NETWORK_DELEGATED', 'ADRIVER_DELEGATED'];
        },

        accessLevel () {
            return this.user().
                isAdmin() ? this.tr('Superadmin access') : this.role().
                    get('name');
        },
        isHiddenAccess () {
            return Boolean(this.role().
                get('is_hidden'));
        },

        accountCurrency () {
            const account = this.account();
            if (account) {
                const currency_id = account.get('currency_id');

                // MCC accounts could not have currency
                if (currency_id) {
                    return this.currencies().
                        get(currency_id);
                }
            }
            return null;
        },

        connectionCurrency () {
            const connection = this.connection();
            if (connection) {
                const currency_id = connection.get('currency_id');
                if (currency_id) {
                    return this.currencies().
                        get(currency_id);
                }
            }
            return null;
        },

        currencySymbol (currency) {
            if (currency) {
                return currency.get('symbol');
            }
        },

        withSymbol (label, currencySymbol) {
            const currency = currencySymbol || (
                this.accountCurrency() ? this.accountCurrency().
                    get('symbol') : null);
            return currency ? '{0},{1}{2}'.format(label, Utils.nbsp, currency) : label;
        },

        currencyIdByCode (code) {
            let currency = this.currencies().
                where({code});
            currency = _.isEmpty(currency) ? null : currency[0];
            return currency && currency.id;
        },

        accountManager () {
            const account = this.account();
            if (account && account.get('real_manager_email')) {
                return {
                    email: account.get('real_manager_email'),
                    name: account.get('real_manager_name')
                };
            }
            return null;

        },

        projectUrl (name, args, queryParams) {
            const extArgs = args || {};
            extArgs.account_id = this.account().
                get('id');
            extArgs.project_id = this.project().
                get('id');
            return this.url(name, extArgs, queryParams);
        },

        idExists (id) {
            return !_.isNull(id) && !_.isUndefined(id);
        },

        authToken () {
            return this.store.getters['common/authToken'];
        },

        setAuthToken (token, options) {
            console.error('Attempt to set auth toke in appContext');
            return $.cookie('token', token, options);
        },

        getHeadersObject (options) {
            let accountId = null;

            const headers = new Headers();
            options || (options = {});
            headers.append('X-API-Token', this.authToken());
            if (!_.isUndefined(options.accountId)) {
                accountId = options.accountId;
            } else if (this.account()) {
                accountId = this.accountId();
            }

            if (this.idExists(accountId)) {
                headers.append('X-API-Account', accountId);
            }
            return headers;
        },

        getRequestHeaders (options) {
            let accountId = null;
            const headers = {};
            options = options || {};
            headers['X-API-Token'] = this.authToken();
            if (!_.isUndefined(options.accountId)) {
                accountId = options.accountId;
            } else if (this.account()) {
                accountId = this.accountId();
            }

            if (this.idExists(accountId)) {
                headers['X-API-Account'] = accountId;
            }
            return headers;
        },

        injectRequestHeaders (xhr, options) {
            let accountId = null;

            options || (options = {});
            xhr.setRequestHeader('X-API-Token', this.authToken());

            if (!_.isUndefined(options.accountId)) {
                accountId = options.accountId;
            } else if (this.account()) {
                accountId = this.accountId();
            }

            if (this.idExists(accountId)) {
                xhr.setRequestHeader('X-API-Account', accountId);
            }
        },

        urlContext (context) {
            return _.extend({
                account_id: this.accountId(),
                project_id: this.projectId(),
                placement_id: this.placementId(),
                order_id: this.orderId(),
                contract_id: this.contractId(),
                connection_id: this.connectionId(),
                bid_manager_group_id: this.bidManagerGroupId()
            }, context);
        },

        externalUrlParams (params) {
            params = params || {};

            return $.param(params);
        },

        channelByName (channelName) {
            return this.channels().
                find(
                    channel => channel.get('name') === channelName || channel.slug() === channelName
                );
        },

        setRouter (router) {
            this.set({router});
            this.routerDeferred.resolve();
        },

        // Backwards compatibility

        getUrlContext () {
            return this.urlContext();
        },

        getCurrentUser () {
            return this.user();
        },
        getChannels () {
            return new Backbone.Collection(
                this.channels().
                    filter(function (value) {
                        return !_.contains(this.trackingOnlyChannels(), value.get('id'));
                    }, this)
            );
        },

        fetchCurrentAccount (id) {
            return this.fetch({account_id: id});
        },
        setCurrentAccount (id) {
            return this.fetchCurrentAccount(id);
        },

        fetchCurrentProject (id) {
            return this.fetch({project_id: id});
        },
        setCurrentProject (id) {
            return this.fetchCurrentProject(id);
        },

        showEmptyHelp () {
            let v = this.get('showEmptyHelp');
            if (!_.isUndefined(v)) {
                return v;
            }
            v = $.cookie('show_empty_help') === 'true';
            this.set('showEmptyHelp', v);
            return v;
        },

        releaseNotesSeen (releaseNoteId) {
            this.userNotes().
                releaseNotesSeen(releaseNoteId);
        },

        onboardingSeen (onboardingId, meta = {}) {
            this.userNotes().
                onboardingSeen(onboardingId, meta);
        },

        showOnboarding () {

            /*
             * const {id: onboardingId, ...onboardingOptions} = params;
             * if (!onboardingId) {
             *   return;
             * }
             * const onboarding = this.userNotes().getOnboarding(onboardingId);
             * if (onboarding.seen) {
             *   return;
             * }
             * return this.onboarding.run(onboardingId, onboardingOptions);
             */
        },

        dateFormatter () {

        },
        numeralDelimeters () {

        },

        currentUserLocale () {
            let locale = 'en-EU';
            if (this.getCurrentUser().
                get('locale') === 'ru') {
                locale = 'ru-RU';
            }
            return locale;
        },

        getItemsOnPage () {
            return Number($.cookie('items_on_page')) || 10;
        },
        setItemsOnPage (n) {
            $.cookie('items_on_page', n, {expires: this.cookiesExpireTime});
        },

        vatDivider (date) {
            return this.getVatDividerForDate(date || moment());
        },
        withVat () {
            const withVat = $.cookie('with_vat');
            return _.isNull(withVat) ? this.user().
                get('with_vat') : withVat === 'true';
        },
        setWithVat (value) {
            const oldValue = this.withVat();
            $.cookie('with_vat', value, {expires: this.cookiesExpireTime});
            if (oldValue !== value) {
                this.trigger('changeWithVat');
            }
        },
        getVatDividerForDate (date) {
            const defaultVat = 1;
            const vats = this.account().
                get('vats') || [];

            date = moment(date || undefined);
            for (let i = vats.length - 1; i >= 0; i = i - 1) {
                const vat = vats[i];
                if (moment(vat.date) <= date) {
                    return new BigNumber(vat.value);
                }
            }
            console.warn('[VAT] Rate not found for {0}. Using 1 as VAT'.format(date.format('YYYY-MM-DD')));
            return new BigNumber(defaultVat);
        },

        basePath () {
            return this.get('base_path');
        },
        baseImageUrl () {
            return `${this.basePath()}img/`;
        },
        baseCssUrl () {
            return `${this.basePath()}css/`;
        },

        isDevelopment () {
            // eslint-disable-next-line no-undef
            return DEBUG;
        },

        brandLogo () {
            return this.user().
                get('logo');
        },

        brandLogoSmall () {
            return this.user().
                get('logo_small');
        },

        helpUrl () {
            return this.user().
                get('help_url');
        },

        /*
         * App storage value has higher priority than localStorageRegistry.
         * Set value:
         * Value sets into both storages: app and localStorageRegistry unless option 'storeAppOnly: true' is not passed.
         * Get value:
         * Fallback: app storage -> localStorageRegistry -> specific context-dependent default value.
         * It's used for per tab settings storing. But after tab reload, localStorageRegistry value will be used.
         */
        getStorageValue (name) {
            let value = sessionStorage.getItem(name);
            if (value) {
                return JSON.parse(value);
            }

            let localStorageModel = this.localStorageRegistry[name];
            if (!localStorageModel) {
                localStorageModel = new LocalStorage({}, {id: name});
                this.localStorageRegistry[name] = localStorageModel;
            }

            value = localStorageModel.toJSON();

            // We return value if json is not empty.
            if (!_.isEmpty(value)) {
                return value;
            }
        },

        setStorageValue (name, value, options) {
            options = options || {};
            sessionStorage.setItem(name, JSON.stringify(value));

            if (options.storeAppOnly) {
                return;
            }

            let localStorageModel = this.localStorageRegistry[name];
            if (localStorageModel) {
                localStorageModel.clear({silent: !_.isEmpty(value)}).
                    set(value);
            } else {
                localStorageModel = new LocalStorage({}, {id: name});
                this.localStorageRegistry[name] = localStorageModel;
                localStorageModel.save(value);
            }

            return this;
        },

        getLocalizedRegions (channelId) {
            return this.get(localizedRegionsKey.format(channelId, this.user().
                get('locale')));
        },

        setLocalizedRegions (channelId, regions) {
            return this.set(localizedRegionsKey.format(channelId, this.user().
                get('locale')), regions);
        },

        isSidebarClosed () {
            const sidebarStorage = this.getStorageValue('sidebar') || {};
            return Boolean(sidebarStorage.isClosed);
        },

        setSidebarClosed (value) {
            const sidebarStorage = this.getStorageValue('sidebar') || {};
            sidebarStorage.isClosed = value;
            this.setStorageValue('sidebar', sidebarStorage);
        }
    });

    return new AppContext();
});

